<script setup lang="ts">
import { loadStripe } from '@stripe/stripe-js'
import { useEventQuery } from '@/enitites/event/api/queries'
import { useImage } from '@/shared/composables/image'
import { computed, onMounted, ref } from 'vue'
// @ts-expect-error Not a ts file
import store from '@/store'
import {
  useCashTierChangeMutation,
  useChangeTicketTierMutation,
  useStripeTierChangeMutation,
} from '@/features/tickets/api/queries'
import BKInput from '@/components/AppShared/BKInput.vue'
import useValidationRules from '@/shared/composables/validation'
import { centsToDollars } from '@/services/currency'
import { ICashUpgradeRequest } from '@/features/tickets/types'

const props = defineProps<{
  eventId: number
  ticketId: number
  newTierId: number
  isAdmin?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'tier-changed'): void
}>()

const { getImageById } = useImage()
const rules = useValidationRules()
const getUserDB = computed(() => store.getters.getUserDB)
const { data: event, isLoading: isLoadingEvent, isError: isEventError } = useEventQuery(props.eventId)
const {
  mutate: postChange,
  isPending: isPostingChange,
  error: errorPostingChange,
  data: tierChangeQuote,
} = useChangeTicketTierMutation()
const {
  mutate: postStripeTierChange,
  isPending: isPostingCheckout,
  error: errorPostingCheckout,
} = useStripeTierChangeMutation(props.eventId)
const {
  mutate: postCashTierChange,
  isPending: isPostingCashPayment,
  error: errorPostingCashPayment,
} = useCashTierChangeMutation(props.eventId)

const isFormValid = ref(true)
const stripeError = ref<string | null>(null)
const isPaymentInitialized = ref(false)
const contactName = ref(getUserDB.value?.dancerName || '')
const contactEmail = ref(getUserDB.value?.aEmail || getUserDB.value?.parentEmail || '')

const initPaymentElement = async (clientSecret: string) => {
  isPaymentInitialized.value = true

  let stripe
  try {
    stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
  } catch (error) {
    stripeError.value = (error as Error)?.message || 'An error occurred while loading payment gateway.'
    isPaymentInitialized.value = false
    return
  }

  if (!stripe) {
    stripeError.value = 'Failed to load payment gateway.'
    return
  }

  let checkout
  try {
    stripeError.value = null
    checkout = await stripe.initEmbeddedCheckout({ clientSecret })
  } catch (error) {
    stripeError.value = (error as Error)?.message || 'An error occurred while initializing payment.'
    isPaymentInitialized.value = false
    return
  }

  if (!checkout) {
    stripeError.value = 'Failed to initialize payment elements.'
    return
  }

  checkout.mount('#payment-element')
}

const handlePurchase = () => {
  if (
    !isFormValid.value ||
    isPostingChange.value ||
    !tierChangeQuote.value ||
    isPostingCheckout.value ||
    isPostingCashPayment.value
  )
    return

  if (props.isAdmin) {
    const tierChangeRequest: ICashUpgradeRequest = {
      newTierId: tierChangeQuote.value.newTierId,
      paymentOption: 'cash',
    }
    postCashTierChange(
      {
        ticketId: props.ticketId,
        data: tierChangeRequest,
      },
      {
        onSuccess: () => {
          emit('tier-changed')
        },
      }
    )
    return
  }

  postStripeTierChange(
    {
      ticketId: props.ticketId,
      data: {
        ...(tierChangeQuote.value || {}),
        contactName: contactName.value,
        contactEmail: contactEmail.value,
      },
    },
    {
      onSuccess: (response) => {
        if ('clientSecret' in response && response.clientSecret) {
          try {
            stripeError.value = null
            initPaymentElement(response.clientSecret)
          } catch (error) {
            stripeError.value = (error as Error)?.message || 'An error occurred while initializing payment.'
          }
        }
      },
    }
  )
}

onMounted(() => {
  if (props.newTierId && props.ticketId) {
    postChange({
      ticketId: props.ticketId,
      data: { newTierId: props.newTierId },
    })
  }
})
</script>

<template>
  <div class="purchase-interface-container">
    <div
      class="purchase-ticket-interface"
      :class="{ 'payment-initialized': isPaymentInitialized }"
    >
      <div class="header">
        <template v-if="isLoadingEvent">
          <v-skeleton-loader
            type="image"
            height="84"
            width="84"
          />
          <div class="skeleton-info">
            <v-skeleton-loader type="heading" />
            <v-skeleton-loader
              type="text@2"
              width="150"
            />
          </div>
        </template>

        <template v-else-if="isEventError">
          <p class="error-message">Error loading event data</p>
        </template>
        <template v-else-if="event">
          <v-img
            :src="getImageById(event.image, 168, 168, 'event')"
            height="84"
            width="84"
            class="event-image"
          />
          <div class="event-info">
            <h2 class="event-title">{{ event.title }}</h2>
            <p class="event-info-row">{{ event.dateFlat }}</p>
            <p class="event-info-row">{{ event.timeFlat }}</p>
          </div>
        </template>
      </div>
      <div class="tickets">
        <div id="payment-element" />
        <div v-if="isAdmin && tierChangeQuote">
          <h4>Check new ticket information:</h4>
          <div class="tier-item">
            <span class="tier-title">
              {{ tierChangeQuote.newTierTitle }}
            </span>
            <span class="tier-price">
              {{ centsToDollars(tierChangeQuote.newTierPrice) }}
            </span>
          </div>
        </div>
        <div
          v-if="!isPaymentInitialized && !isAdmin"
          class="checkout-form"
        >
          <v-form
            v-model="isFormValid"
            class="form-section"
          >
            <p class="section-title">Contact Information</p>
            <BKInput
              v-model="contactName"
              label="Full name"
              placeholder="Enter your full name"
              :rules="[rules.required]"
            />
            <BKInput
              v-model="contactEmail"
              label="Email address"
              placeholder="Enter your email address"
              :rules="[rules.required, rules.email]"
            />
          </v-form>
        </div>
      </div>
      <div
        v-if="!isPaymentInitialized"
        class="actions"
      >
        <BKButton
          outlined
          width="auto"
          @click="$emit('close')"
        >
          Go Back
        </BKButton>
        <BKButton
          white-text
          width="auto"
          color="var(--color-green)"
          :disabled="!isFormValid"
          :loading="isPostingChange || isPostingCheckout || isPostingCashPayment"
          @click="handlePurchase"
        >
          Purchase ticket
        </BKButton>
      </div>
      <div
        v-if="errorPostingChange || errorPostingCheckout || errorPostingCashPayment"
        class="error-message"
      >
        {{ errorPostingChange?.message || errorPostingCheckout?.message || errorPostingCashPayment?.message }}
      </div>
      <div
        v-if="!isPaymentInitialized"
        class="summary-area summary"
        :class="{ loading: isPostingChange }"
      >
        <v-progress-linear
          v-if="isPostingChange"
          indeterminate
          color="var(--color-blue-800)"
          class="progress-indicator"
        />
        <template v-if="tierChangeQuote">
          <p class="summary-title">Order summary</p>
          <ul class="summary-table-tier-change">
            <li class="summary-tier-row">
              <span class="tier-label">Current tier</span>
              <span class="tier-name">{{ tierChangeQuote.oldTierTitle }}</span>
              <span class="tier-amount">{{ centsToDollars(tierChangeQuote.oldTierPricePaid * -1) }}</span>
            </li>
            <li class="summary-tier-row">
              <span class="tier-label">New tier</span>
              <span class="tier-name">{{ tierChangeQuote.newTierTitle }}</span>
              <span class="tier-amount">
                <span
                  v-if="tierChangeQuote.discountAmount"
                  class="tier-amount-old"
                >
                  {{ centsToDollars(tierChangeQuote.newTierPrice + tierChangeQuote.discountAmount) }}
                </span>
                {{ centsToDollars(tierChangeQuote.newTierPrice) }}
              </span>
              <span
                v-if="tierChangeQuote.discountAmount && tierChangeQuote.discountTitle"
                class="tier-discount-title"
              >
                {{ tierChangeQuote.discountTitle }}
              </span>
            </li>
          </ul>
          <v-divider />
          <ul class="subtotal-table">
            <li class="subtotal-row">
              <span class="subtotal-label">Difference</span>
              <span class="subtotal-value">{{ centsToDollars(tierChangeQuote.subtotal) }}</span>
            </li>
            <li class="subtotal-row">
              <span class="subtotal-label">Platform fees</span>
              <span class="subtotal-value">{{ centsToDollars(tierChangeQuote.platformFee) }}</span>
            </li>
          </ul>
          <v-divider />
          <div class="total-row">
            <span class="total-label">Total</span>
            <span class="total-value">{{ centsToDollars(tierChangeQuote.total) }}</span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.purchase-interface-container {
  container-type: inline-size;
}

.purchase-ticket-interface {
  display: grid;
  grid-template-areas:
    'header  summary'
    'tickets summary'
    'actions summary'
    'error   summary';
  grid-template-columns: 1fr 304px;
  grid-template-rows: auto 1fr auto;
  max-height: 90vh;
  overflow: hidden;

  &.payment-initialized {
    grid-template-areas:
      'header'
      'tickets';
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto;
    overflow-y: auto;
    max-height: unset;
  }

  p {
    margin: 0;
  }

  @container (max-width: 800px) {
    grid-template-areas:
      'header'
      'tickets'
      'summary'
      'actions';
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto;
    overflow-y: auto;
    max-height: unset;
  }

  @container (max-width: 600px) {
    padding-top: 50px;
  }
}

.header {
  position: relative;
  grid-area: header;
  display: flex;
  gap: 16px;
  padding: 40px;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 100%;
    height: 3px;
    background-image: linear-gradient(to bottom, rgb(0 0 0 / 10%), transparent);
  }

  .skeleton-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    gap: 8px;
  }

  .event-image {
    flex: 0 0 auto;
    border-radius: 8px;
  }

  .event-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;

    .event-title {
      font-weight: var(--font-weight-semi-bold);
      font-size: 24px;
      line-height: 30px;
      color: var(--color-dark-800);
    }

    .event-info-row {
      font-size: 16px;
      line-height: 24px;
      color: var(--color-grey-300);
    }
  }

  @container (max-width: 600px) {
    padding: 24px 12px;
  }
}

.tickets {
  grid-area: tickets;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 20px;
  padding: 30px 40px;
  overflow-y: auto;

  @container (max-width: 600px) {
    padding: 16px 12px;
  }

  .v-tab {
    text-transform: unset;

    &.v-tab--active {
      font-weight: 700;
    }
  }

  .v-window {
    overflow: visible;
  }

  .tickets-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.checkout-form {
  display: flex;
  flex-direction: column;
  gap: 40px;

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .section-title {
      margin-bottom: 4px;
      font-size: 24px;
      line-height: 30px;
      font-weight: 700;
      color: var(--color-dark-800);
    }

    .discount-code-input-wrapper {
      display: flex;
      gap: 10px;
    }
  }
}

.actions {
  grid-area: actions;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  padding: 30px 40px 40px;
  border-top: 1px solid var(--color-grey-700);

  > * {
    flex: 1 1 0;
    min-width: max(40%, max-content);
  }
}

.summary-area {
  grid-area: summary;
}

.error-message {
  grid-area: error;
  margin: 0 40px 20px;
  font-size: 16px;
  line-height: 24px;
  color: red;
}

.summary {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 40px 20px;
  background-color: var(--color-grey-100);

  &.loading > * {
    opacity: 0.5;
  }

  ul,
  li {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .summary-title {
    font-size: 20px;
    line-height: 26px;
    font-weight: 500;
    color: var(--color-dark-800);
  }

  .summary-table-tier-change {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .summary-tier-row {
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: 0 8px;
      align-items: center;
      font-size: 16px;
      line-height: 24px;
      color: var(--color-dark-800);

      .tier-label {
        font-weight: 500;
        font-size: 14px;
        color: var(--color-grey-300);
      }

      .tier-discount-title {
        grid-column: 1 / 3;
        font-size: 12px;
        line-height: 16px;
        color: var(--color-grey-300);
      }

      .tier-name {
        font-weight: 500;
      }

      .tier-amount {
        align-self: center;
        text-align: right;
        font-weight: 500;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        grid-row: span 2;

        .tier-amount-old {
          font-size: 12px;
          line-height: 16px;
          color: var(--color-grey-300);
          text-decoration: line-through;
        }
      }
    }
  }

  .subtotal-table {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;

    .subtotal-row {
      display: contents;

      .subtotal-label {
        font-size: 16px;
        line-height: 24px;
        color: var(--color-dark-800);
      }

      .subtotal-value {
        font-size: 16px;
        line-height: 24px;
        text-align: right;
        color: var(--color-dark-800);
      }
    }
  }

  .total-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;
    font-size: 16px;
    line-height: 24px;
    font-weight: 700;
    color: var(--color-dark-800);

    .total-value {
      text-align: right;
    }
  }

  .progress-indicator {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    opacity: 1 !important;
  }
}

#payment-element:empty {
  display: none;
}

.tier-item {
  display: grid;
  grid-template: 'title price' / 1fr auto;
  gap: 4px;
  padding: 12px;
  border: 1px solid var(--color-grey-500);
  border-radius: 6px;
  margin-top: 16px;
}

.tier-title {
  grid-area: title;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.3;
  color: var(--color-dark-800);
}

.tier-price {
  grid-area: price;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-grey-300);
}
</style>
